import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import { Alert, DefaultLoader } from "../../components";
import { getNewTokenSuccess } from "../../containers/Authentication/redux/actions";
import { sendLogError } from "../../utils/api";
import { useHandleDuplicate } from "../../utils/hooks";
import { updateCustomerDuplicate } from "../getStarted/redux/actions";
import { checkProfileRedirect, createPayload } from "../getStarted/utils";
import { checkEcddStatus, updateProfile, resetData, getCustomer } from "../redux/action";
import { store } from "../../store";
import {
  getFundDetail,
  getFundDetailFailed,
} from "../fundSelection/redux/action";
import { DEEPLINK, SWITCHING_TYPE } from "../redux/constant";
import { DEFAULT } from "../../utils/constants";
import { getSessionMasterId } from "../../utils/functions/getSessionValues";
import {
  getSessionStorageSync,
  setSessionStorageSync,
} from "../../utils/session";
const PRODUCT_TYPE = ["invest", "save", "retire", SWITCHING_TYPE];
export const COMPLETE_PAYLOAD = "COMPLETE_PAYLOAD";
function DeeplinkLP(props) {
  const {
    history,
    match: { params },
    profile,
    token,
    loading,
    updateCustomerDuplicate,
    updateCustomerDuplicateLoading,
    getNewTokenSuccess,
    checkEcddStatus,
    getFundDetail,
    getFundDetailFailed,
    fundDetail,
    fundDetailLoading,
    updateProfile,
    resetData,
    getCustomer
  } = props;
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [nricAlert, setOpenNricAlert] = useState(false);
  const [pepAlert, setPEPalert] = useState(false);

  const currentPath = history?.location?.pathname;

  const liveAgentWhatsappNumber =
    process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER?.trim();

  const [status, setStatus] = useState("loading");
  const { duplicated, copywriting, handleDuplicate } = useHandleDuplicate(
    updateCustomerDuplicate,
    getNewTokenSuccess,
    () => {
      const latestProfile = store.getState().goal.profile;
      if (latestProfile?.amlHistory?.length > 0) {
        if (params?.plan) {
          return history.push(`/plan/${params.plan}`);
        }

        if (params?.type && params?.code) {
          return history.push(`/deeplink/${params.type}/${params.code}`);
        }

        // return the customer to get started in case there's no paramater
        return history.push("/");
      }

      return history.push("/onBoarding/form-step-one", {
        type: params?.type,
        code: params?.code,
        plan: params?.plan,
      });
    }
  );

  const hasCalledEkyc = useRef(false);
  const hasRefreshedCustomer = useRef(false);
  const isDuplicated =
    duplicated.idNo || duplicated.mobileNo || duplicated.name;

  const hasCalledGetFundDetail = useRef(false);

  const manipulateLogError = amlError => {
    let amlLogError;

    if (amlError && amlError.amlHistory && amlError.amlHistory.length > 0) {
      amlLogError = amlError.amlHistory[0].amlSts;
    }

    let errorPayload = {
      page: "deeplink",
      path: `/deeplink/status/${params?.type}/${params?.code}`,
      aml: amlLogError || "Not Found",
    };

    sendLogError(errorPayload);
  };

  const redirectAction = profile => {
    if (isDuplicated || nricAlert) {
      return;
    }

    let directTo = checkProfileRedirect(profile);
    if (typeof directTo === "object" && !Array.isArray(directTo)) {
      manipulateLogError(profile);

      history.push("/");
      return;
    }

    switch (directTo) {
      case "DIRECT_ONBOARDING":
        history.push("/onBoarding/form-step-one", {
          type: params?.type,
          code: params?.code,
          plan: params?.plan,
        });
        break;
      case "DIRECT_DASHBOARD":
        // A returning customer flagged for profile review must acknowledge or
        // update their profile before cashing in. Remember the deeplink target
        // (type/code/plan) so /profile/info can resume them on their cash-in
        // screen after the update, then route them to review their profile
        // first. Mirrors the direct app-login behaviour in getStarted.
        if (profile?.showProfileUpdatePrompt) {
          setSessionStorageSync("deeplinkProfileResume", {
            type: params?.type,
            code: params?.code,
            plan: params?.plan,
          });
          history.push("/profile/info");
          break;
        }
        if (params?.type?.toLowerCase() === "invest" || params?.plan) {
          history.push("/deeplink/invest-introduction", {
            type: params?.type,
            code: params?.code,
            plan: params?.plan,
          });
        } else if (
          params?.type?.toLowerCase() === "save" ||
          params?.type?.toLowerCase() === "retire" ||
          params?.type?.toLowerCase() === SWITCHING_TYPE
        ) {
          history.push(`/deeplink/${params?.type}/${params?.code}`);
        }
        break;
      case COMPLETE_PAYLOAD:
        if ("pepFlag" in profile && profile.pepFlag === "1") {
          setPEPalert(true);
          break;
        }
        const payload = createPayload(profile);

        if (typeof payload === "string" && payload === "invalid-email") {
          history.push("/onBoarding/form-step-one", {
            type: params?.type,
            code: params?.code,
            invalidEmail: true,
          });
          break;
        }

        if (!isUpdatingProfile) {
          setIsUpdatingProfile(true);
          updateProfile(
            {
              payloadData: payload,
              from: {
                from: DEEPLINK,
                value: { type: params?.type, code: params?.code },
              },
            },
            () => setIsUpdatingProfile(false)
          );
        }
        break;
      default:
        manipulateLogError(profile);
        break;
    }
  };

  useEffect(() => {
    const checkRedirection = async () => {
      const masterId = await getSessionMasterId();
      const lowerType = params?.type?.toLowerCase();
      const needsFundDetail =
        lowerType === "invest" || lowerType === SWITCHING_TYPE;
      if (isUpdatingProfile || (needsFundDetail && fundDetailLoading)) return;
      if (profile && token && profile?.masterId === masterId) {
        // `showProfileUpdatePrompt` is a backend flag that can flip to true days
        // after this session cached the customer. The cached profile here is
        // either the persisted (up to 6-days-stale) record or the lightweight
        // TnG auth payload — neither reflects the current flag. So ALWAYS pull a
        // fresh /mp/customer once per deeplink visit BEFORE deciding the route,
        // then let the refreshed profile re-trigger this effect to decide on
        // up-to-date data. Not gated on amlHistory: the old gate skipped the
        // refresh whenever the cached profile lacked amlHistory (e.g. the TnG
        // auth payload for plan deeplinks), which is exactly why plan deeplinks
        // decided on stale data and never showed the profile-review screen.
        if (!hasRefreshedCustomer.current) {
          hasRefreshedCustomer.current = true;
          getCustomer();
          return;
        }
        redirectAction(profile);

        if (profile?.amlHistory?.length > 0) {
          checkEcddStatus(profile.masterId);
        }
        return;
      }
    };
    checkRedirection();
  }, [profile, token, isUpdatingProfile, fundDetailLoading]);

  /**
   * This useEffect is to get the fund detail for invest and switching type deeplinks.
   * It will check if the token is available and the type is invest or switching,
   * then it will call the getFundDetail action to get the fund detail.
   * For invest, if the code is default, it will call the getFundDetailFailed action to set the fund detail to null
   * and move forward to invest dashboard.
   * For switching, the fund code (the source fund to switch from) is always required.
   */
  useEffect(() => {
    if (!token || !params) return;
    const lowerType = params?.type?.toLowerCase();

    if (lowerType === "invest") {
      if (params?.code?.toUpperCase() === DEFAULT) {
        // This logic is to set fundLoading flag false and move forward to invest dashboard
        getFundDetailFailed();
      } else {
        getFundDetail({ fundCd: params?.code, showDisabled: true });
      }
      return;
    }

    if (lowerType === SWITCHING_TYPE && params?.code) {
      getFundDetail({ fundCd: params?.code, showDisabled: true });
    }
  }, [token, params]);

  // Records that a fund-detail fetch has started, so the deeplinkAuth guard
  // below can distinguish "still loading" from "fund genuinely not found".
  useEffect(() => {
    if (fundDetailLoading) hasCalledGetFundDetail.current = true;
  }, [fundDetailLoading]);

  useEffect(() => {
    const deeplinkAuth = async () => {
      const masterId = await getSessionMasterId();
      if (hasCalledEkyc.current) return;

      if (!profile || !masterId || profile?.masterId !== masterId) {
        window.my.postMessage({
          api: "getAuthCode",
          scopes: ["auth_base", "auth_user", "USER_INFO_EKYC", "FIN_PROFILE"],
          event: "getAccessToken",
        });
        hasCalledEkyc.current = true;
        return;
      }

      const typeIsNotExist = !params?.type;

      const productTypeIsNotExist = !PRODUCT_TYPE.includes(
        params?.type?.toLowerCase()
      );

      // Read fund state fresh from the store: the async await above can leave
      // the captured props stale while a fund fetch is still in flight, which
      // would make the guard redirect before the fetch resolves.
      const fundState = store.getState()?.fundSelectionReducer || {};
      const liveFundLoading = fundState.fundDetailLoading;
      const liveFundDetail = fundState.fundDetailByCode;

      // Only treat an empty fund as "not exist" once a fetch has actually
      // started and finished; on resume the fetch is still pending so empty
      // != missing.
      const fundIsNotExist =
        params?.code?.toUpperCase() === DEFAULT
          ? false
          : hasCalledGetFundDetail.current &&
          !liveFundLoading &&
          (!liveFundDetail ||
            (typeof liveFundDetail === "object" &&
              Object.values(liveFundDetail).length === 0));

      // During a session-expiry resume the fund fetch fails purely because the
      // token expired, leaving fund detail empty. That empty state must not be
      // read as "fund not found": the guard below would redirect to Get Started
      // and pull the customer off the deeplink before re-auth can resume them.
      // The fetch is retried automatically once handleDataCustSuccess resumes
      // this page, so skip the redirect entirely while a resume is pending.
      const resumePending =
        getSessionStorageSync("deeplinkResumePending") === "1";

      if (
        !resumePending &&
        ((currentPath.includes("deeplink") &&
          (typeIsNotExist || productTypeIsNotExist || fundIsNotExist)) ||
          (currentPath.includes("plan") && !params?.plan))
      ) {
        history.push("/");
        return;
      }
    };
    deeplinkAuth();
  }, [
    params,
    fundDetail,
    fundDetailLoading,
    profile,
    hasCalledEkyc.current,
    hasCalledGetFundDetail.current,
  ]);

  useEffect(() => {
    const lowerType = params?.type?.toLowerCase();
    const needsFundDetail =
      lowerType === "invest" || lowerType === SWITCHING_TYPE;

    if (loading || (needsFundDetail && fundDetailLoading)) {
      setStatus("loading");
    } else if (!loading || (needsFundDetail && !fundDetailLoading)) {
      setStatus("idle");
    }
  }, [loading, fundDetailLoading, params]);

  useEffect(() => {
    window.eventManager.on("NRIC_VALIDATION", () => {
      setOpenNricAlert(true);
    });

    window.eventManager.on("EKYC_TERMINATED", () => {
      setStatus("ekyc-terminated");
    });

    window.eventManager.on("EKYC_LOADING_SHOWED", () => {
      setStatus("ekyc-showed");
    });

    return () => window.eventManager.clearEvents();
  }, []);

  if (status === "loading") return <DefaultLoader />;

  if (status === "ekyc-terminated") {
    history.push("/");
    return null;
  }

  return (
    <>
      <Alert
        title="Invalid ID format"
        description="We encountered an issue with the ID format that was automatically captured. "
        isOpen={nricAlert}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          setOpenNricAlert(false);
          window.my.call("openAppLink", {
            url: `https://wa.me/${liveAgentWhatsappNumber}`,
          });
        }}
        handleCancel={() => setOpenNricAlert(false)}
        withCloseButton
      />
      <Alert
        title="Account doesn’t meet requirement"
        description="So sorry, your account doesn’t meet our requirement."
        isOpen={pepAlert}
        isCS="https://principal.com.my"
        isCancel={false}
        handleAction={() => {
          resetData();
          setPEPalert(false);
          history.push("/");
        }}
      />
      <Alert
        title={copywriting.heading}
        description={
          <>
            {copywriting.title}
            <br />
            <br />
            {copywriting.description}
            <br />
            <br />
            {copywriting.additionalContext}
            <span
              style={{
                marginTop: "1rem",
                marginBottom: "0.5rem",
                fontSize: "16px",
                fontWeight: 700,
                display: "block",
              }}
            >
              {duplicated.name && <>Name: {duplicated.name}</>}
              {duplicated.mobileNo && (
                <>
                  {duplicated.name && <br />}
                  Mobile: {duplicated.mobileNo}
                </>
              )}
              {duplicated.idNo && (
                <>
                  {(duplicated.name || duplicated.mobileNo) && <br />}
                  MyKad: {duplicated.idNo}
                </>
              )}
            </span>
          </>
        }
        additionalContent={
          duplicated.type === "tng" && (
            <p
              style={{
                color: "#1278CC",
                fontSize: "16px",
                fontWeight: 400,
                width: "100%",
                textAlign: "center",
                cursor: "pointer",
                display: "inline-block",
                marginBottom: "24px",
              }}
              onClick={() => {
                window.my.navigateTo({
                  url:
                    "/pages/link/link?url=" +
                    encodeURIComponent(
                      "https://support.tngdigital.com.my/hc/en-my/requests/new?ticket_form_id=360001558914"
                    ),
                });
              }}
            >
              Contact Touch ‘n Go Customer Service
            </p>
          )
        }
        isOpen={isDuplicated}
        handleAction={handleDuplicate}
        btnTxt={
          updateCustomerDuplicateLoading
            ? "Please wait..."
            : { mp: "Contact Customer Service", tng: "Confirm" }[
            duplicated.type
            ]
        }
        submitButtonDisabled={updateCustomerDuplicateLoading}
        titleStyle={{ fontWeight: 900 }}
      />
    </>
  );
}

const withConnect = connect(
  state => ({
    profile: state.goal.profile,
    loading: state.goal.loading,
    token: state.auth.MPtoken,
    fundDetail: state.fundSelectionReducer.fundDetailByCode,
    fundDetailLoading: state.fundSelectionReducer.fundDetailLoading,
    ecddStatus: state.goal.ecddStatus,
    updateCustomerDuplicateLoading:
      state.getStartedReducer.updateCustomerDuplicateLoading,
  }),
  {
    updateCustomerDuplicate,
    getNewTokenSuccess,
    checkEcddStatus,
    getFundDetail,
    getFundDetailFailed,
    resetData,
    updateProfile,
    getCustomer,

  }
);

export default compose(
  withRouter,
  withConnect
)(DeeplinkLP);
